import { useEffect, useRef, useState } from 'react';
import { CheckCircle, Clock, Target, ExternalLink } from 'lucide-react';

const milestones = [
  {
    phase: 'Phase 1',
    status: 'complete',
    label: 'Complete',
    title: 'Foundation Built',
    items: [
      'Smart Router — 5/5 unit tests passing',
      '100% decisions audit-logged',
      'Routing in <100ms confirmed',
      '6 live white-labeled demos running',
    ],
  },
  {
    phase: 'Phase 2',
    status: 'active',
    label: 'Active Now',
    title: 'Pilot & LOI Push',
    items: [
      'Community bank pilot conversations active',
      'Target: 3–5 signed LOIs by EOY 2026',
      'Seeking $5M Seed to compress 18 months → 9',
      '50+ institutions on platform by EOY Year 2',
    ],
  },
  {
    phase: 'Phase 3–4',
    status: 'upcoming',
    label: 'Roadmap',
    title: 'Scale',
    items: [
      '$4M+ ARR run-rate at end of period',
      'Full GENIUS Act compliance layer',
      'Stablecoin + Tokenized Deposit GA',
      '$240M ARR target by 2030',
    ],
  },
];

const proof = [
  { value: '6', label: 'Live White-Label Demos' },
  { value: 'Phase 2', label: 'Currently Active' },
  { value: '<100ms', label: 'Smart Router Speed' },
  { value: '5/5', label: 'Router Tests Passing' },
];

export default function TractionSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setRevealed(true); obs.disconnect(); } },
      { threshold: 0.05 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="traction"
      data-testid="traction-section"
      ref={sectionRef}
      className="relative section-padding bg-black"
      style={{ scrollMarginTop: '80px' }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,255,0,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,0,0.02) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.04), transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 relative">

        {/* Header */}
        <div
          className="mb-12"
          style={{ opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease' }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-[2px] bg-[#00FF00]" />
            <span className="text-[#00FF00] font-mono text-[11px] uppercase tracking-[0.4em]">Where We Are</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-none"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}>
            Traction &
            <br /><span style={{ color: '#00FF00', textShadow: '0 0 30px rgba(0,255,0,0.3)' }}>Momentum</span>
          </h2>
        </div>

        {/* Proof bar */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1A1A1A] mb-12"
          style={{ opacity: revealed ? 1 : 0, transition: 'opacity 0.7s ease 0.1s' }}
        >
          {proof.map((p, i) => (
            <div
              key={p.label}
              data-testid={`traction-proof-${i}`}
              className="bg-black px-6 py-5 text-center"
            >
              <div className="font-black font-mono text-2xl text-white mb-1"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: '#00FF00' }}>
                {p.value}
              </div>
              <div className="text-[#A3A3A3] text-xs uppercase tracking-widest font-mono">{p.label}</div>
            </div>
          ))}
        </div>

        {/* Phase timeline */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {milestones.map((m, i) => (
            <div
              key={m.phase}
              data-testid={`traction-phase-${i}`}
              className="border p-6 relative overflow-hidden"
              style={{
                background: m.status === 'active' ? 'rgba(0,255,0,0.04)' : '#0A0A0A',
                borderColor: m.status === 'complete' ? 'rgba(0,255,0,0.3)' : m.status === 'active' ? '#00FF00' : '#1A1A1A',
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0)' : 'translateY(30px)',
                transition: `all 0.6s ease ${0.2 + i * 0.12}s`,
                boxShadow: m.status === 'active' ? '0 0 30px rgba(0,255,0,0.08)' : 'none',
              }}
            >
              {m.status === 'active' && (
                <div className="absolute top-0 left-0 right-0 h-[2px]"
                  style={{ background: 'linear-gradient(90deg, transparent, #00FF00, transparent)' }} />
              )}

              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">{m.phase}</span>
                <div className="flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider"
                  style={{
                    borderColor: m.status === 'complete' ? 'rgba(0,255,0,0.4)' : m.status === 'active' ? '#00FF00' : '#333',
                    color: m.status === 'complete' ? '#00FF00' : m.status === 'active' ? '#00FF00' : '#555',
                    background: m.status === 'active' ? 'rgba(0,255,0,0.08)' : 'transparent',
                  }}>
                  {m.status === 'complete' && <CheckCircle size={9} />}
                  {m.status === 'active' && <Clock size={9} />}
                  {m.status === 'upcoming' && <Target size={9} />}
                  {m.label}
                </div>
              </div>

              <div className="text-white font-bold text-xs uppercase mb-4"
                style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {m.title}
              </div>

              <div className="flex flex-col gap-2">
                {m.items.map(item => (
                  <div key={item} className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0"
                      style={{ background: m.status === 'complete' || m.status === 'active' ? '#00FF00' : '#333' }} />
                    <span className="text-[#A3A3A3] text-xs leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Live demo CTA */}
        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-6 border border-[#00FF00] border-opacity-30 p-6"
          style={{
            background: 'rgba(0,255,0,0.03)',
            opacity: revealed ? 1 : 0,
            transition: 'opacity 0.7s ease 0.6s',
          }}
        >
          <div>
            <div className="text-[#00FF00] font-mono text-[10px] uppercase tracking-widest mb-1">Live Now</div>
            <div className="text-white font-bold text-sm uppercase" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
              See the white-label demo running at mybank.nexgenpay.co
            </div>
            <div className="text-[#A3A3A3] text-xs mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Full wallet UI — your brand, your rails, your control. Live environment.
            </div>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <a
              href="https://mybank.nexgenpay.co"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="traction-live-demo-btn"
              className="flex items-center gap-2 btn-outline-neon text-xs"
              style={{ fontFamily: 'Unbounded, sans-serif', padding: '12px 20px' }}
            >
              View Live Demo <ExternalLink size={12} />
            </a>
            <button
              onClick={() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="traction-request-demo-btn"
              className="btn-neon text-xs"
              style={{ fontFamily: 'Unbounded, sans-serif', padding: '12px 20px' }}
            >
              Request a Demo
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
